import { useRef, useState, type ReactNode } from 'react';
import { Ellipsis, FileDown, FileSpreadsheet, Printer } from 'lucide-react';
import { InfoTip, type InfoContent } from './InfoTip';
import { exportCsv, exportXlsx, type ExportData } from '../lib/export';
import { useClickOutside } from '../lib/useClickOutside';

export type Span = 3 | 4 | 5 | 6 | 7 | 8 | 12;

export interface CardProps {
  title?: string;
  subtitle?: ReactNode;
  info?: InfoContent;
  span?: Span;
  flush?: boolean;
  actions?: ReactNode;
  footer?: ReactNode;
  /** Data behind the card for the "…" menu (CSV / Excel). */
  exportData?: () => ExportData;
  className?: string;
  children?: ReactNode;
}

export function Card({
  title,
  subtitle,
  info,
  span,
  flush,
  actions,
  footer,
  exportData,
  className,
  children,
}: CardProps) {
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, menu, () => setMenu(false));
  const name = title ?? 'export';
  return (
    <section className={['card', span ? `span-${span}` : '', flush ? 'flush' : '', className ?? ''].join(' ')}>
      {(title || actions || exportData) && (
        <header className="card-head">
          <div style={{ minWidth: 0 }}>
            {title && (
              <h3 className="card-title" style={{ margin: 0 }}>
                {title}
                {info && <InfoTip info={info} />}
              </h3>
            )}
            {subtitle && <div className="card-sub">{subtitle}</div>}
          </div>
          <div className="card-actions" ref={ref}>
            {actions}
            {exportData && (
              <button
                type="button"
                className="icon-btn"
                aria-label={`${name}: export`}
                aria-expanded={menu}
                onClick={() => setMenu((m) => !m)}
              >
                <Ellipsis size={16} />
              </button>
            )}
            {menu && exportData && (
              <div className="popover" role="menu" style={{ top: 34, right: 0 }}>
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  onClick={() => {
                    exportCsv(exportData(), name);
                    setMenu(false);
                  }}
                >
                  <FileDown size={15} /> Export CSV
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  onClick={() => {
                    void exportXlsx(exportData(), name);
                    setMenu(false);
                  }}
                >
                  <FileSpreadsheet size={15} /> Export Excel
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  onClick={() => {
                    setMenu(false);
                    window.print();
                  }}
                >
                  <Printer size={15} /> Print
                </button>
              </div>
            )}
          </div>
        </header>
      )}
      <div className="card-body">{children}</div>
      {footer && <footer className="card-foot">{footer}</footer>}
    </section>
  );
}

export function Grid({ children }: { children: ReactNode }) {
  return <div className="grid">{children}</div>;
}

export function Section({ title, sub, info }: { title: string; sub?: string; info?: InfoContent }) {
  return (
    <div className="section">
      <h2>{title}</h2>
      {info && <InfoTip info={info} />}
      {sub && <span className="t-foot">{sub}</span>}
    </div>
  );
}
